// Generated by OrchestKit Claude Plugin
// Created: 2026-10-04

import { chargerPdf } from './exporter-pdf.ts';
import { formaterFcfa } from './montant.tsx';
import { montantEnLettres } from './nombre-en-lettres.ts';
import { dessinerEnteteFacture } from './exporter-facture-pdf-entete.ts';
import { dessinerBlocMecef } from './exporter-facture-pdf-mecef.ts';
import type { IdentiteBoutique, LigneFacture, ReglementFacture } from './exporter-facture-pdf-types.ts';

export type { IdentiteBoutique, LigneFacture, ReglementFacture } from './exporter-facture-pdf-types.ts';

/**
 * Facture PDF — refonte du 04/10/2026, sur la maquette fournie.
 *
 * Deux éléments de la maquette sont volontairement NEUTRALISÉS pour l'instant :
 *
 * - Le bloc e-MECeF/QR (norme DGI Bénin, `exporter-facture-pdf-mecef.ts`)
 *   est écrit mais jamais dessiné (`AFFICHER_MECEF = false`) : la
 *   facturation normalisée n'est pas encore raccordée côté API (code
 *   MECeF, NIM, compteurs n'existent nulle part). À activer le jour où ces
 *   données existent.
 * - La colonne « Taxe » (groupe A/B, TVA) de la maquette n'existe pas ici :
 *   aucun taux de taxe n'est encore modélisé côté vente.
 *
 * L'IFU (boutique ou client) ne s'affiche QUE s'il est fourni — voir
 * `exporter-facture-pdf-entete.ts`.
 */
const AFFICHER_MECEF: boolean = false;

/** Lit `lastAutoTable.finalY` — le typage de jspdf-autotable ne l'expose pas. */
function finY(doc: import('jspdf').jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
}

export async function exporterFacturePdf({
  numero,
  boutique,
  dateHeure,
  statut,
  motifContrepassation,
  vendeur,
  client,
  ifuClient,
  canal,
  lignes,
  sousTotal,
  remisePied,
  arrondi,
  total,
  paiements,
  encaisse,
  resteDu,
  nomFichier,
}: {
  numero: string;
  boutique: IdentiteBoutique;
  dateHeure: string;
  statut: 'Validée' | 'Contrepassée';
  motifContrepassation?: string | null;
  vendeur: string;
  client: string;
  ifuClient?: string | null;
  canal: string;
  lignes: LigneFacture[];
  sousTotal: number;
  remisePied: number;
  arrondi: number;
  total: number;
  paiements: ReglementFacture[];
  encaisse: number;
  resteDu: number;
  nomFichier: string;
}): Promise<{ blob: Blob; nomFichier: string }> {
  const { jsPDF, autoTable } = await chargerPdf();
  const doc = new jsPDF({ unit: 'mm' });
  const largeur = doc.internal.pageSize.getWidth();
  const marge = 14;
  const noir = [22, 32, 43] as const;
  const gris = [91, 103, 118] as const;
  const trait = [221, 227, 234] as const;

  const curseur = dessinerEnteteFacture(doc, {
    marge,
    largeur,
    numero,
    boutique,
    dateHeure,
    statut,
    motifContrepassation,
    vendeur,
    client,
    ifuClient,
    canal,
    total,
    resteDu,
  });

  // ── Lignes — Taxe non modélisée, délibérément absente (voir en-tête du fichier) ──
  autoTable(doc, {
    startY: curseur,
    margin: { left: marge, right: marge },
    head: [['Désignation', 'Qté', 'P.U.', 'Remise', 'Montant']],
    body: lignes.map((ligne) => [
      ligne.imeis !== undefined && ligne.imeis.length > 0
        ? `${ligne.designation}\n${ligne.reference} · IMEI ${ligne.imeis.join(', ')}`
        : `${ligne.designation}\n${ligne.reference}`,
      String(ligne.quantite),
      formaterFcfa(ligne.prixUnitaire),
      ligne.remise > 0 ? formaterFcfa(-ligne.remise) : '—',
      formaterFcfa(ligne.total),
    ]),
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [247, 249, 251], textColor: [91, 103, 118], fontStyle: 'bold', fontSize: 8 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    didParseCell: (donnees) => {
      // La référence (et l'IMEI) tiennent sur la seconde ligne de la
      // cellule, en plus petit — jsPDF-autotable n'a pas de rich-text,
      // ce styling grossier (taille de police unique par cellule) est la
      // limite acceptée plutôt qu'une dépendance de plus pour ça.
      if (donnees.column.index === 0 && donnees.cell.section === 'body') {
        donnees.cell.styles.fontSize = 8.5;
      }
    },
  });

  // ── Règlements + Totaux, côte à côte ────────────────────────────────────
  const yApresTable = finY(doc) + 8;
  const largeurReglements = (largeur - marge * 2) * 0.48;
  const xTotaux = marge + largeurReglements + 10;
  const largeurTotaux = largeur - marge - xTotaux;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...noir);
  doc.text('Règlements', marge, yApresTable);

  let yReglements = yApresTable + 6;
  if (paiements.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...gris);
    doc.text('Entièrement à crédit.', marge, yReglements);
    yReglements += 6;
  } else {
    for (const paiement of paiements) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...noir);
      doc.text(`${paiement.mode} · ${paiement.dateHeure}`, marge, yReglements);
      doc.setFont('helvetica', 'bold');
      doc.text(formaterFcfa(paiement.montant), marge + largeurReglements, yReglements, { align: 'right' });
      yReglements += 6;
    }
  }

  const ligneTotal = (libelle: string, valeur: string, y: number, gras = false): void => {
    doc.setFont('helvetica', gras ? 'bold' : 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...noir);
    doc.text(libelle, xTotaux, y);
    doc.text(valeur, largeur - marge, y, { align: 'right' });
  };

  let yTotaux = yApresTable;
  ligneTotal('Sous-total', formaterFcfa(sousTotal), yTotaux);
  yTotaux += 6;
  if (remisePied > 0) {
    ligneTotal('Remises', formaterFcfa(-remisePied), yTotaux);
    yTotaux += 6;
  }
  ligneTotal('Arrondi', formaterFcfa(arrondi), yTotaux);
  yTotaux += 7;

  doc.setFillColor(0, 113, 189);
  doc.rect(xTotaux - 4, yTotaux - 5, largeurTotaux + 4, 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text('Total TTC', xTotaux, yTotaux + 1.5);
  doc.text(formaterFcfa(total), largeur - marge, yTotaux + 1.5, { align: 'right' });
  yTotaux += 14;

  ligneTotal('Encaissé', formaterFcfa(encaisse), yTotaux, true);
  yTotaux += 6;
  if (resteDu > 0) {
    doc.setTextColor(212, 42, 58);
    ligneTotal('Reste dû', formaterFcfa(resteDu), yTotaux, true);
    doc.setTextColor(...noir);
    yTotaux += 6;
  }

  const yMontantLettres = Math.max(yReglements, yTotaux) + 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...gris);
  const phrase = `Arrêtée la présente facture à la somme de : ${montantEnLettres(total)}.`;
  const lignesPhrase = doc.splitTextToSize(phrase, largeur - marge * 2) as string[];
  doc.text(lignesPhrase, marge, yMontantLettres);

  if (AFFICHER_MECEF) {
    dessinerBlocMecef(doc, marge, largeur, yMontantLettres + lignesPhrase.length * 4.5 + 6);
  }

  // ── Pied de page ─────────────────────────────────────────────────────────
  const hauteur = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...trait);
  doc.line(marge, hauteur - 14, largeur - marge, hauteur - 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...gris);
  doc.text('Merci de votre confiance.', marge, hauteur - 9);
  doc.text(
    `Généré le ${new Date().toLocaleString('fr-FR', { timeZone: 'Africa/Porto-Novo' })}`,
    largeur - marge,
    hauteur - 9,
    { align: 'right' },
  );

  return { blob: doc.output('blob'), nomFichier };
}
